import React, { useState, useContext, useEffect, useCallback } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { startRegistration } from "@simplewebauthn/browser";
import { AuthContext } from "../helpers/AuthContext";
import { loadConfig } from "../helpers/config";
import NotificationSettings from "../components/NotificationSettings";
import { generateKeyPair, exportPublicKey, downloadPrivateKey } from "../helpers/licenseImageCrypto";

function Profile() {
    const { authState } = useContext(AuthContext);
    const [profileError, setProfileError] = useState("");
    const [profileSuccess, setProfileSuccess] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [passwordSuccess, setPasswordSuccess] = useState("");
    const [newPasswordValue, setNewPasswordValue] = useState("");
    const [profileData, setProfileData] = useState({
        fullName: "",
        phoneNumber: "",
    });
    const [apiUrl, setApiUrl] = useState("");
    const [loadingConfig, setLoadingConfig] = useState(true);
    const [loadingProfile, setLoadingProfile] = useState(true);
    const [passkeys, setPasskeys] = useState([]);
    const [loadingPasskeys, setLoadingPasskeys] = useState(true);
    const [passkeyError, setPasskeyError] = useState("");
    const [passkeySuccess, setPasskeySuccess] = useState("");
    const [registeringPasskey, setRegisteringPasskey] = useState(false);
    const [deletingPasskeyId, setDeletingPasskeyId] = useState(null);
    const passkeySupported =
        typeof window !== "undefined" &&
        typeof window.PublicKeyCredential !== "undefined";

    // Key management state
    const [hasPublicKey, setHasPublicKey] = useState(null); // null = loading
    const [keyGenerating, setKeyGenerating] = useState(false);
    const [keyAlert, setKeyAlert] = useState(null);

    // Check password requirements
    const checkPasswordRequirements = (password) => {
        return {
            minLength: password.length >= 8,
            hasLowercase: /[a-z]/.test(password),
            hasUppercase: /[A-Z]/.test(password),
            hasNumber: /[0-9]/.test(password),
            hasSpecial: /[^a-zA-Z0-9]/.test(password),
        };
    };

    const passwordRequirements = checkPasswordRequirements(newPasswordValue);

    const fetchProfile = useCallback(() => {
        if (!apiUrl) return;
        axios.get(`${apiUrl}/auth/profile`, {
            headers: { accessToken: localStorage.getItem("accessToken") },
        }).then((response) => {
            setProfileData({
                fullName: response.data.fullName || "",
                phoneNumber: response.data.phoneNumber || "",
            });
            setLoadingProfile(false);
        }).catch((error) => {
            console.error("Error fetching profile:", error);
            setProfileError("Fehler beim Laden des Profils");
            setLoadingProfile(false);
        });
    }, [apiUrl]);

    const fetchKeyStatus = useCallback(() => {
        if (!apiUrl) return;
        axios.get(`${apiUrl}/auth/public-key`, {
            headers: { accessToken: localStorage.getItem("accessToken") },
        }).then((res) => {
            setHasPublicKey(!!res.data.publicKey);
        }).catch(() => setHasPublicKey(false));
    }, [apiUrl]);

    useEffect(() => {
        async function fetchConfig() {
            const config = await loadConfig();
            setApiUrl(config.APIURL);
            setLoadingConfig(false);
        }
        fetchConfig();
    }, []);

    const fetchPasskeys = useCallback(() => {
        if (!apiUrl) return;
        axios.get(`${apiUrl}/auth/webauthn/credentials`, {
            headers: { accessToken: localStorage.getItem("accessToken") },
        }).then((response) => {
            setPasskeys(response.data);
            setLoadingPasskeys(false);
        }).catch((error) => {
            console.error("Error fetching passkeys:", error);
            setLoadingPasskeys(false);
        });
    }, [apiUrl]);

    useEffect(() => {
        if (!loadingConfig && apiUrl) {
            fetchProfile();
            fetchKeyStatus();
            fetchPasskeys();
        }
    }, [loadingConfig, apiUrl, fetchProfile, fetchPasskeys, fetchKeyStatus]);

    const handleRegisterPasskey = async () => {
        setPasskeyError("");
        setPasskeySuccess("");
        setRegisteringPasskey(true);
        try {
            const { data: options } = await axios.post(
                `${apiUrl}/auth/webauthn/register/options`,
                {},
                { headers: { accessToken: localStorage.getItem("accessToken") } }
            );
            const attestationResponse = await startRegistration({ optionsJSON: options });
            const deviceName = window.prompt(
                "Wie soll dieser Passkey heißen? (z. B. \"Mein iPhone\")",
                "Passkey"
            );
            await axios.post(
                `${apiUrl}/auth/webauthn/register/verify`,
                { attestationResponse, name: deviceName || "Passkey" },
                { headers: { accessToken: localStorage.getItem("accessToken") } }
            );
            setPasskeySuccess("Passkey erfolgreich hinzugefügt");
            fetchPasskeys();
        } catch (error) {
            console.error("Passkey registration error:", error);
            if (error.name === "NotAllowedError") {
                // User cancelled the passkey prompt
            } else {
                setPasskeyError(
                    error.response?.data?.error || `Passkey konnte nicht hinzugefügt werden (${error.name}: ${error.message})`
                );
            }
        }
        setRegisteringPasskey(false);
    };

    const handleDeletePasskey = (id) => {
        setPasskeyError("");
        setPasskeySuccess("");
        setDeletingPasskeyId(id);
        axios.delete(`${apiUrl}/auth/webauthn/credentials/${id}`, {
            headers: { accessToken: localStorage.getItem("accessToken") },
        }).then(() => {
            setPasskeySuccess("Passkey entfernt");
            fetchPasskeys();
        }).catch((error) => {
            setPasskeyError(error.response?.data?.error || "Passkey konnte nicht entfernt werden");
        }).finally(() => {
            setDeletingPasskeyId(null);
        });
    };

    const profileValidationSchema = Yup.object().shape({
        fullName: Yup.string().max(100, "Name darf maximal 100 Zeichen lang sein"),
        phoneNumber: Yup.string().max(20, "Telefonnummer darf maximal 20 Zeichen lang sein"),
    });

    const onProfileSubmit = (data, { setSubmitting }) => {
        axios.put(`${apiUrl}/auth/profile`, data, {
            headers: { accessToken: localStorage.getItem("accessToken") },
        }).then((response) => {
            if (response.data.error) {
                setProfileError(response.data.error);
                setProfileSuccess("");
            } else {
                setProfileSuccess("Profil erfolgreich aktualisiert");
                setProfileError("");
            }
            setSubmitting(false);
        }).catch((error) => {
            setProfileError("Fehler beim Aktualisieren des Profils");
            setProfileSuccess("");
            setSubmitting(false);
        });
    };

    const passwordValidationSchema = Yup.object().shape({
        currentPassword: Yup.string().required("Aktuelles Passwort ist erforderlich"),
        newPassword: Yup.string()
            .min(8, "Passwort muss mindestens 8 Zeichen lang sein")
            .matches(/[a-z]/, "Passwort muss mindestens einen Kleinbuchstaben enthalten")
            .matches(/[A-Z]/, "Passwort muss mindestens einen Großbuchstaben enthalten")
            .matches(/[0-9]/, "Passwort muss mindestens eine Ziffer enthalten")
            .matches(/[^a-zA-Z0-9]/, "Passwort muss mindestens ein Sonderzeichen enthalten")
            .required("Neues Passwort ist erforderlich"),
        confirmPassword: Yup.string()
            .oneOf([Yup.ref('newPassword'), null], "Passwörter müssen übereinstimmen")
            .required("Bestätigung ist erforderlich"),
    });

    const onPasswordSubmit = (data, { resetForm, setSubmitting }) => {
        axios.put(`${apiUrl}/auth/password`, {
            currentPassword: data.currentPassword,
            newPassword: data.newPassword,
        }, {
            headers: { accessToken: localStorage.getItem("accessToken") },
        }).then((response) => {
            if (response.data.error) {
                setPasswordError(response.data.error);
                setPasswordSuccess("");
            } else {
                setPasswordSuccess("Passwort erfolgreich geändert");
                setPasswordError("");
                setNewPasswordValue("");
                resetForm();
            }
            setSubmitting(false);
        }).catch((error) => {
            const errorMsg = error.response?.data?.error || "Fehler beim Ändern des Passworts";
            setPasswordError(errorMsg);
            setPasswordSuccess("");
            setSubmitting(false);
        });
    };

    const handleGenerateKeyPair = async () => {
        setKeyGenerating(true);
        setKeyAlert(null);
        try {
            const keyPair = await generateKeyPair();
            const pubKeyB64 = await exportPublicKey(keyPair.publicKey);
            await axios.put(`${apiUrl}/auth/public-key`, { publicKey: pubKeyB64 }, {
                headers: { accessToken: localStorage.getItem("accessToken") },
            });
            await downloadPrivateKey(keyPair.privateKey, 'fwminv-private.key');
            setHasPublicKey(true);
            setKeyAlert({
                type: 'success',
                message: 'Schlüsselpaar erfolgreich erstellt. Der Private Key wurde heruntergeladen – bitte sicher auf einem USB-Stick speichern!',
            });
        } catch (err) {
            console.error(err);
            setKeyAlert({ type: 'error', message: 'Fehler beim Erstellen des Schlüsselpaares: ' + (err.message || err) });
        } finally {
            setKeyGenerating(false);
        }
    };

    if (loadingConfig || loadingProfile) {
        return <div className="loading"><span>Lade Profil...</span></div>;
    }

    return (
        <div className="admin-page">
            <div className="admin-header">
                <h1>👤 Mein Profil</h1>
                <p>Verwalten Sie Ihre persönlichen Informationen und Einstellungen</p>
            </div>

            <div className="admin-content">
                {/* User Info Card */}
                <div className="admin-section">
                    <div className="section-header">
                        <h2>📋 Kontoinformationen</h2>
                    </div>
                    <div className="form-card">
                        <div className="form-group">
                            <label><strong>Benutzername:</strong></label>
                            <div style={{ padding: '10px', background: '#f8f9fa', borderRadius: '4px' }}>
                                {authState.username}
                            </div>
                        </div>
                        <div className="form-group">
                            <label><strong>Rolle:</strong></label>
                            <div style={{ padding: '10px', background: '#f8f9fa', borderRadius: '4px' }}>
                                {authState.isAdmin ? "🛡️ Administrator" : "👤 Benutzer"}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Personal Information Section */}
                <div className="admin-section">
                    <div className="section-header">
                        <h2>ℹ️ Persönliche Informationen</h2>
                        <p className="section-description">
                            Aktualisieren Sie Ihren Namen und Ihre Telefonnummer
                        </p>
                    </div>

                    {/* Profile Messages */}
                    {profileError && (
                        <div className="alert alert-error" style={{ margin: '20px 30px 0' }}>
                            <strong>❌ Fehler:</strong> {profileError}
                        </div>
                    )}
                    {profileSuccess && (
                        <div className="alert alert-success" style={{ margin: '20px 30px 0' }}>
                            <strong>✓ Erfolg:</strong> {profileSuccess}
                        </div>
                    )}

                    <div className="form-card">
                        <Formik
                            initialValues={profileData}
                            enableReinitialize={true}
                            onSubmit={onProfileSubmit}
                            validationSchema={profileValidationSchema}
                        >
                            {({ isSubmitting }) => (
                                <Form>
                                    <div className="form-group">
                                        <label>Vollständiger Name:</label>
                                        <ErrorMessage name="fullName" component="span" className="error-message" />
                                        <Field
                                            autoComplete="off"
                                            name="fullName"
                                            placeholder="Vor- und Nachname"
                                            className="form-control"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Telefonnummer:</label>
                                        <ErrorMessage name="phoneNumber" component="span" className="error-message" />
                                        <Field
                                            autoComplete="off"
                                            name="phoneNumber"
                                            placeholder="+49 123 456789"
                                            className="form-control"
                                        />
                                    </div>

                                    <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                                        {isSubmitting ? "💾 Wird gespeichert..." : "💾 Speichern"}
                                    </button>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>

                {/* Password Change Section */}
                <div className="admin-section">
                    <div className="section-header">
                        <h2>🔒 Passwort ändern</h2>
                        <p className="section-description">
                            Ändern Sie Ihr Passwort für mehr Sicherheit
                        </p>

                        {/* Password Messages */}
                        {passwordError && (
                            <div className="alert alert-error" style={{ margin: '20px 30px 0' }}>
                                <strong>❌ Fehler:</strong> {passwordError}
                            </div>
                        )}
                        {passwordSuccess && (
                            <div className="alert alert-success" style={{ margin: '20px 30px 0' }}>
                                <strong>✓ Erfolg:</strong> {passwordSuccess}
                            </div>
                        )}

                    </div>
                    <div className="form-card">
                        <Formik
                            initialValues={{
                                currentPassword: "",
                                newPassword: "",
                                confirmPassword: "",
                            }}
                            onSubmit={onPasswordSubmit}
                            validationSchema={passwordValidationSchema}
                        >
                            {({ isSubmitting, setFieldValue }) => (
                                <Form>
                                    <div className="form-group">
                                        <label>Aktuelles Passwort:</label>
                                        <ErrorMessage name="currentPassword" component="span" className="error-message" />
                                        <Field
                                            autoComplete="current-password"
                                            type="password"
                                            name="currentPassword"
                                            placeholder="Aktuelles Passwort"
                                            className="form-control"
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label>Neues Passwort:</label>
                                        <ErrorMessage name="newPassword" component="span" className="error-message" />
                                        <Field
                                            autoComplete="new-password"
                                            type="password"
                                            name="newPassword"
                                            placeholder="Neues Passwort"
                                            className="form-control"
                                            onChange={(e) => {
                                                setFieldValue('newPassword', e.target.value);
                                                setNewPasswordValue(e.target.value);
                                            }}
                                        />

                                        {newPasswordValue && (
                                            <div style={{
                                                marginTop: '15px',
                                                padding: '15px',
                                                background: '#f8f9fa',
                                                borderRadius: '6px',
                                                border: '1px solid #dee2e6'
                                            }}>
                                                <strong style={{ display: 'block', marginBottom: '10px', color: '#495057' }}>🔒 Passwort-Stärke:</strong>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <span style={{
                                                            fontSize: '18px',
                                                            color: passwordRequirements.minLength ? '#28a745' : '#dc3545'
                                                        }}>
                                                            {passwordRequirements.minLength ? '✔' : '❌'}
                                                        </span>
                                                        <span style={{ color: passwordRequirements.minLength ? '#28a745' : '#6c757d' }}>
                                                            Mindestens 8 Zeichen
                                                        </span>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <span style={{
                                                            fontSize: '18px',
                                                            color: passwordRequirements.hasLowercase ? '#28a745' : '#dc3545'
                                                        }}>
                                                            {passwordRequirements.hasLowercase ? '✔' : '❌'}
                                                        </span>
                                                        <span style={{ color: passwordRequirements.hasLowercase ? '#28a745' : '#6c757d' }}>
                                                            Mindestens ein Kleinbuchstabe (a-z)
                                                        </span>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <span style={{
                                                            fontSize: '18px',
                                                            color: passwordRequirements.hasUppercase ? '#28a745' : '#dc3545'
                                                        }}>
                                                            {passwordRequirements.hasUppercase ? '✔' : '❌'}
                                                        </span>
                                                        <span style={{ color: passwordRequirements.hasUppercase ? '#28a745' : '#6c757d' }}>
                                                            Mindestens ein Großbuchstabe (A-Z)
                                                        </span>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <span style={{
                                                            fontSize: '18px',
                                                            color: passwordRequirements.hasNumber ? '#28a745' : '#dc3545'
                                                        }}>
                                                            {passwordRequirements.hasNumber ? '✔' : '❌'}
                                                        </span>
                                                        <span style={{ color: passwordRequirements.hasNumber ? '#28a745' : '#6c757d' }}>
                                                            Mindestens eine Ziffer (0-9)
                                                        </span>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <span style={{
                                                            fontSize: '18px',
                                                            color: passwordRequirements.hasSpecial ? '#28a745' : '#dc3545'
                                                        }}>
                                                            {passwordRequirements.hasSpecial ? '✔' : '❌'}
                                                        </span>
                                                        <span style={{ color: passwordRequirements.hasSpecial ? '#28a745' : '#6c757d' }}>
                                                            Mindestens ein Sonderzeichen (!@#$%^&*)
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="form-group">
                                        <label>Passwort bestätigen:</label>
                                        <ErrorMessage name="confirmPassword" component="span" className="error-message" />
                                        <Field
                                            autoComplete="new-password"
                                            type="password"
                                            name="confirmPassword"
                                            placeholder="Neues Passwort wiederholen"
                                            className="form-control"
                                        />
                                    </div>

                                    <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                                        {isSubmitting ? "🔄 Wird geändert..." : "🔒 Passwort ändern"}
                                    </button>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>

                {/* Passkeys Section */}
                {passkeySupported && (
                    <div className="admin-section">
                        <div className="section-header">
                            <h2>🔑 Passkeys</h2>
                            <p className="section-description">
                                Melden Sie sich ohne Passwort an, z. B. mit dem Fingerabdruck oder Gesicht Ihres Smartphones
                            </p>
                        </div>

                        {passkeyError && (
                            <div className="alert alert-error" style={{ margin: '20px 30px 0' }}>
                                <strong>❌ Fehler:</strong> {passkeyError}
                            </div>
                        )}
                        {passkeySuccess && (
                            <div className="alert alert-success" style={{ margin: '20px 30px 0' }}>
                                <strong>✓ Erfolg:</strong> {passkeySuccess}
                            </div>
                        )}

                        <div className="form-card">
                            {loadingPasskeys ? (
                                <p>Lade Passkeys...</p>
                            ) : passkeys.length === 0 ? (
                                <p>Noch keine Passkeys registriert.</p>
                            ) : (
                                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px' }}>
                                    {passkeys.map((passkey) => (
                                        <li
                                            key={passkey.id}
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                padding: '10px',
                                                background: '#f8f9fa',
                                                borderRadius: '4px',
                                                marginBottom: '8px',
                                            }}
                                        >
                                            <span>
                                                {passkey.name || "Passkey"}
                                                <span style={{ color: '#6c757d', marginLeft: '8px', fontSize: '0.85em' }}>
                                                    hinzugefügt am {new Date(passkey.createdAt).toLocaleDateString('de-DE')}
                                                </span>
                                            </span>
                                            <button
                                                type="button"
                                                className="btn btn-danger btn-sm"
                                                disabled={deletingPasskeyId === passkey.id}
                                                onClick={() => handleDeletePasskey(passkey.id)}
                                            >
                                                {deletingPasskeyId === passkey.id ? "Wird entfernt..." : "Entfernen"}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}

                            <button
                                type="button"
                                className="btn btn-primary"
                                disabled={registeringPasskey}
                                onClick={handleRegisterPasskey}
                            >
                                {registeringPasskey ? "🔄 Wird hinzugefügt..." : "➕ Passkey hinzufügen"}
                            </button>
                        </div>
                    </div>
                )}
                {/* Encryption Key Section */}
                <div className="admin-section">
                    <div className="section-header">
                        <h2>🔐 Verschlüsselungsschlüssel</h2>
                        <p className="section-description">
                            Für das verschlüsselte Hochladen von Führerscheinfotos benötigen Sie ein persönliches Schlüsselpaar.
                            Der Private Key wird als Datei heruntergeladen und muss auf einem USB-Stick gespeichert werden.
                        </p>
                    </div>
                    <div className="form-card">
                        {keyAlert && (
                            <div className={`alert alert-${keyAlert.type === 'success' ? 'success' : 'error'}`} style={{ marginBottom: '1rem' }}>
                                {keyAlert.type === 'success' ? <strong>✓ Erfolg:</strong> : <strong>❌ Fehler:</strong>} {keyAlert.message}
                            </div>
                        )}

                        <div className="form-group">
                            <label><strong>Status:</strong></label>
                            <div style={{ padding: '10px', background: '#f8f9fa', borderRadius: '4px' }}>
                                {hasPublicKey === null
                                    ? 'Wird geprüft…'
                                    : hasPublicKey
                                        ? '✅ Public Key ist auf dem Server hinterlegt'
                                        : '⚠️ Kein Schlüsselpaar vorhanden'}
                            </div>
                        </div>

                        <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: '4px', padding: '12px', marginBottom: '1rem', fontSize: '0.875rem' }}>
                            <strong>⚠️ Wichtig:</strong> Nach der Generierung wird der Private Key einmalig heruntergeladen.
                            Er wird <strong>nicht</strong> im Browser oder auf dem Server gespeichert.
                            Bewahren Sie ihn sicher auf einem USB-Stick auf – ohne ihn können verschlüsselte Bilder nicht entschlüsselt werden.
                            {hasPublicKey && <span> Ein neues Schlüsselpaar macht alle bestehenden verschlüsselten Bilder <strong>unzugänglich</strong>.</span>}
                        </div>

                        <button
                            className="btn btn-primary"
                            onClick={handleGenerateKeyPair}
                            disabled={keyGenerating}
                        >
                            {keyGenerating
                                ? '🔄 Wird erstellt…'
                                : hasPublicKey
                                    ? '🔄 Neues Schlüsselpaar generieren'
                                    : '🔑 Schlüsselpaar generieren'}
                        </button>
                    </div>
                </div>

                {/* Notification Settings Section */}
                <NotificationSettings />
            </div>
        </div>
    );
}

export default Profile;
